<script lang="ts">
  import type { ReportCardPageData, ReportCardContentSection } from "$lib/api";
  import HistoryIcon from "@lucide/svelte/icons/history";

  let { data, showHeader = true, historyHref = null, accountName = null }: {
    data: ReportCardPageData;
    showHeader?: boolean;
    historyHref?: string | null;
    accountName?: string | null;
  } = $props();

  // Track which sections have their "Improve your grade?" expanded
  let openSections = $state<Record<string, boolean>>({});

  const groups = $derived(
    [...(data.content?.report_card_factory_groups ?? [])].sort((a, b) => a.display_order - b.display_order)
  );
  const ungroupedSections = $derived(
    [...(data.content?.ungrouped_report_card_factory_sections ?? data.content?.report_card_factory_sections ?? [])].sort((a, b) => a.display_order - b.display_order)
  );
  const noData = $derived(data.no_data || !data.content);

  function getGradeClass(grade: string | null, size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'lg'): string {
    if (!grade) return `grade-${size} grade-unknown-${size}`;
    const letter = grade.charAt(0).toLowerCase();
    const known = ['a', 'b', 'c', 'd', 'f'].includes(letter) ? letter : 'unknown';
    return `grade-${size} grade-${known}-${size}`;
  }

  function gradeLetter(grade: string): string {
    return grade.charAt(0).toUpperCase();
  }

  function gradeModifier(grade: string): string {
    return grade.slice(1).replace('-', '\u2212');
  }

  function sectionKey(section: ReportCardContentSection): string {
    return `section-${section.id}`;
  }

  function isSectionOpen(key: string): boolean {
    return openSections[key] ?? false;
  }

  function setSectionOpen(key: string, open: boolean): void {
    openSections = { ...openSections, [key]: open };
  }

  function toggleSection(key: string): void {
    setSectionOpen(key, !isSectionOpen(key));
  }
</script>

<div class="max-w-7xl space-y-6">
  {#if noData}
    {#if data.reference_date}
      {@const d = new Date(data.reference_date + 'T00:00:00')}
      {@const reportingMonth = new Date(d.getFullYear(), d.getMonth() - 1, 1)}
      <div class="rounded-xl border bg-muted/50 px-6 py-8 text-center text-muted-foreground">
        Sorry! No {data.factory_name} report card for {reportingMonth.toLocaleDateString("en-US", { year: "numeric", month: "long" })} yet.
      </div>
    {:else}
      <div class="rounded-xl border bg-muted/50 px-6 py-8 text-center text-muted-foreground">
        Sorry! No {data.factory_name} report card available yet.
      </div>
    {/if}

  {:else}
    {#if showHeader}
      <!-- Header: centered title, account name, date, description -->
      <header class="text-center">
        <h1 class="text-2xl font-bold tracking-tight">{data.factory_name}</h1>
        {#if data.demo && data.demo_source !== 'account_history'}
          <p class="mt-0.5 text-xl text-muted-foreground">Demo Account</p>
          <p class="mt-0.5 text-lg text-muted-foreground">demo date</p>
        {:else}
          {#if accountName}
            <p class="mt-0.5 text-xl text-muted-foreground">{accountName}</p>
          {/if}
          {#if data.reference_date}
            {@const d = new Date(data.reference_date + 'T00:00:00')}
            <p class="mt-0.5 text-lg text-muted-foreground">
              {#if data.quarter_label}
                {@const endMonth = new Date(d.getFullYear(), d.getMonth() - 1, 1)}
                {@const qStartIdx = Math.floor(endMonth.getMonth() / 3) * 3}
                {@const startMonth = new Date(endMonth.getFullYear(), qStartIdx, 1)}
                data for {data.quarter_label} ({startMonth.toLocaleDateString("en-US", { month: "short" })}-{endMonth.toLocaleDateString("en-US", { month: "short" })})
              {:else if data.factory_slug?.includes('monthly')}
                {@const endMonth = new Date(d.getFullYear(), d.getMonth() - 1, 1)}
                {@const startMonth = new Date(d.getFullYear(), d.getMonth() - 3, 1)}
                data for {startMonth.toLocaleDateString("en-US", { month: "short" })}-{endMonth.toLocaleDateString("en-US", { month: "short", year: "numeric" })}
              {:else}
                {@const reportingMonth = new Date(d.getFullYear(), d.getMonth() - 1, 1)}
                data for {reportingMonth.toLocaleDateString("en-US", { year: "numeric", month: "long" })}
              {/if}
            </p>
          {/if}
        {/if}
        {#if data.factory_description}
          <p class="mt-4 text-base text-muted-foreground leading-snug">{data.factory_description}</p>
        {/if}
      </header>
    {/if}

    <!-- Groups (Funnel Stages) -->
    <div class="flex flex-col gap-6 text-left">
      {#each groups as group (group.id)}
        <div class="w-full border-2 border-gray-200 rounded-xl overflow-hidden">
          <!-- Stage header -->
          <div class="px-6 py-3 bg-gray-100 border-b border-gray-200 flex items-center gap-3">
            <div>
              <h3 class="text-lg font-bold text-foreground uppercase tracking-wide">{group.name}</h3>
              {#if group.description}
                <p class="text-sm text-muted-foreground leading-snug mt-0.5">{group.description}</p>
              {/if}
            </div>
          </div>

          <!-- Sections within this group -->
          {#each [...(group.report_card_factory_sections ?? [])].sort((a, b) => a.display_order - b.display_order) as section (section.id)}
            {@const key = sectionKey(section)}
            <div class="p-4 border-b border-gray-100 last:border-b-0">
              <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                <!-- Section grade -->
                <div class="{getGradeClass(section.grade, 'xl')} !mx-0 sm:!mx-auto self-start sm:self-auto">
                  {#if section.grade}
                    <span class="leading-none">{gradeLetter(section.grade)}</span>
                    {#if gradeModifier(section.grade)}
                      <span class="text-[0.85em] self-start leading-none mt-[0.25em] ml-[0.08em]">{gradeModifier(section.grade)}</span>
                    {/if}
                  {:else}
                    <span class="leading-none text-[0.6em]">NA</span>
                  {/if}
                </div>

                <!-- Section content -->
                <div class="flex-1 min-w-0">
                  <h4 class="text-lg font-semibold text-foreground">{section.name}</h4>
                  {#each section.summary ?? [] as paragraph}
                    <p class="text-base text-muted-foreground mt-1.5 leading-normal">{paragraph}</p>
                  {/each}
                </div>
              </div>

              <!-- Improve your grade trigger -->
              {#if section.details?.content?.items?.length}
                <div class="flex justify-end mt-3">
                  <button
                    type="button"
                    class="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-muted-foreground border border-border rounded-md cursor-pointer hover:bg-muted/50 transition-colors"
                    onclick={() => toggleSection(key)}
                  >
                    <span>{isSectionOpen(key) ? 'Hide' : 'Improve your grade?'}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" height="10" width="10" viewBox="0 0 320 512" class="shrink-0 transition-transform {isSectionOpen(key) ? 'rotate-[270deg]' : 'rotate-90'}">
                      <path fill="currentColor" d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"/>
                    </svg>
                  </button>
                </div>
              {/if}
            </div>

            <!-- Expandable content: improvement suggestions -->
            {#if section.details?.content?.items?.length && isSectionOpen(key)}
              <div class="mr-4 -mt-2 mb-4 ml-0 rounded-lg bg-gray-100 px-4 py-3 sm:ml-20">
                <h5 class="text-base font-semibold text-foreground">{section.details.header}</h5>
                <p class="text-base text-muted-foreground mt-1">{section.details.content.header}</p>
                <ul class="list-disc pl-5 mt-2 space-y-1">
                  {#each section.details.content.items as item}
                    <li class="text-base text-muted-foreground leading-normal">{item}</li>
                  {/each}
                </ul>
              </div>
            {/if}
          {/each}
        </div>
      {/each}
    </div>

    <!-- Ungrouped sections (rendered when no groups or as standalone sections) -->
    {#if !groups.length && ungroupedSections.length}
      <div class="w-full border-2 border-gray-200 rounded-xl overflow-hidden text-left">
        {#each ungroupedSections as section, i (section.id)}
          {@const key = sectionKey(section)}
          <div class="p-4 {i > 0 ? 'border-t border-gray-200' : ''}">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
              <!-- Section grade -->
              <div class="{getGradeClass(section.grade, 'xl')} !mx-0 sm:!mx-auto self-start sm:self-auto">
                {#if section.grade && section.grade.trim()}
                  <span class="leading-none">{gradeLetter(section.grade)}</span>
                  {#if gradeModifier(section.grade)}
                    <span class="text-[0.85em] self-start leading-none mt-[0.25em] ml-[0.08em]">{gradeModifier(section.grade)}</span>
                  {/if}
                {:else}
                  <span class="leading-none text-[0.6em]">NA</span>
                {/if}
              </div>

              <!-- Section content -->
              <div class="flex-1 min-w-0">
                <h4 class="text-lg font-semibold text-foreground">{section.name}</h4>
                {#each section.summary ?? [] as paragraph}
                  <p class="text-base text-muted-foreground mt-1.5 leading-normal">{paragraph}</p>
                {/each}
              </div>
            </div>

            <!-- Improve your grade (collapsible) -->
            {#if section.details?.content?.items?.length}
              <div class="flex justify-end mt-3">
                <button
                  type="button"
                  class="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-muted-foreground border border-border rounded-md cursor-pointer hover:bg-muted/50 transition-colors"
                  onclick={() => toggleSection(key)}
                >
                  <span>{isSectionOpen(key) ? 'Hide' : 'Improve your grade?'}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" height="10" width="10" viewBox="0 0 320 512" class="shrink-0 transition-transform {isSectionOpen(key) ? 'rotate-[270deg]' : 'rotate-90'}">
                    <path fill="currentColor" d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"/>
                  </svg>
                </button>
              </div>
              {#if isSectionOpen(key)}
                <div class="mr-4 mt-2 mb-1 ml-0 rounded-lg bg-gray-100 px-4 py-3 sm:ml-20">
                  <h5 class="text-base font-semibold text-foreground">{section.details.header}</h5>
                  <p class="text-base text-muted-foreground mt-1">{section.details.content.header}</p>
                  <ul class="list-disc pl-5 mt-2 space-y-1">
                    {#each section.details.content.items as item}
                      <li class="text-base text-muted-foreground leading-normal">{item}</li>
                    {/each}
                  </ul>
                </div>
              {/if}
            {/if}
          </div>
        {/each}
      </div>
    {/if}

    <!-- History link (bottom of card) -->
    {#if historyHref}
      <div class="flex justify-center pt-2">
        <a
          href={historyHref}
          class="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors"
        >
          <HistoryIcon class="size-4" />
          See how your grades have changed over time
        </a>
      </div>
    {/if}
  {/if}
</div>
